import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import esLocale from '@fullcalendar/core/locales/es';
import itLocale from '@fullcalendar/core/locales/it';
import deLocale from '@fullcalendar/core/locales/de';
import plLocale from '@fullcalendar/core/locales/pl';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClock, faUser} from '@fortawesome/free-regular-svg-icons';
import {
  faArrowLeft,
  faArrowRight,
  faBorderAll,
  faCalendarDay,
  faCalendarDays,
  faCalendarWeek,
  faCalendarXmark,
  faCar,
  faChevronLeft,
  faChevronRight,
  faListUl,
  faLock,
  faPeopleGroup,
  faPlus,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import {faGoogle} from '@fortawesome/free-brands-svg-icons';
import API from '../services/API';
import Utils from '../services/Utils';
import PageHeader from '../components/PageHeader';
import BookingModal from '../components/BookingModal';
import BookingCreateModal from '../components/BookingCreateModal';
import GoogleModal from '../components/GoogleModal';

// Couleurs V2 par statut — source unique pour les puces du calendrier, les cartes
// de l'agenda et la légende du pied de carte.
const STATUS_COLORS = {
  CONFIRMED: {color: '#16A34A', soft: 'rgba(22, 163, 74, .12)'},
  VALIDATED: {color: '#F59E0B', soft: 'rgba(245, 158, 11, .14)'},
  EXPIRED: {color: '#DC2626', soft: 'rgba(220, 38, 38, .10)'},
  CANCELED: {color: '#98A1B5', soft: 'rgba(152, 161, 181, .16)'}
};
const DEFAULT_COLOR = {color: '#2A6FDB', soft: 'rgba(42, 111, 219, .10)'};
const colorOf = (status) => STATUS_COLORS[status] || DEFAULT_COLOR;
// Variables CSS consommées par .ccv2-ev / .ccv2-agenda-item.
const styleOf = (status) => {
  const {color, soft} = colorOf(status);
  return {'--ev': color, '--ev-soft': soft};
};

const LEGEND = [
  {status: 'CONFIRMED', labelId: 'CONFIRMED'},
  {status: 'VALIDATED', labelId: 'WAITING_CONFIRMATION'},
  {status: 'EXPIRED', labelId: 'EXPIRED'},
  {status: 'CANCELED', labelId: 'CANCELED'}
];

// « agenda » et « year » sont des vues maison ; seules WEEK et MONTH sont rendues
// par FullCalendar. dayGridYear a été écarté : c'est une grille continue de 53
// lignes, sans séparation de mois, dont FullCalendar étire les cellules.
const VIEWS = [
  {key: 'agenda', labelId: 'AGENDA', icon: faListUl},
  {key: 'dayGridWeek', labelId: 'WEEK', icon: faCalendarWeek},
  {key: 'dayGridMonth', labelId: 'MONTH', icon: faCalendarDays},
  {key: 'year', labelId: 'YEAR', icon: faBorderAll}
];
const isCalendarView = (view) => view === 'dayGridWeek' || view === 'dayGridMonth';

// Langues de l'app : étiquette Intl (formatage des dates) et locale FullCalendar.
const LOCALE_TAGS = {fr: 'fr-FR', en: 'en-GB', es: 'es-ES', it: 'it-IT', de: 'de-DE', pl: 'pl-PL'};
const CALENDAR_LOCALES = {
  fr: frLocale, en: enLocale, es: esLocale,
  it: itLocale, de: deLocale, pl: plLocale
};

// En dessous de ce seuil le calendrier à 7 colonnes n'est plus lisible : la vue Mois
// dégrade ses puces en barres et la vue Semaine passe en défilement (cf. SCSS).
const NARROW_QUERY = '(max-width: 767.98px)';
const isNarrowNow = () => !!(window.matchMedia && window.matchMedia(NARROW_QUERY).matches);

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const ymd = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
// Les dates du planning sont des chaînes « nues » (yyyy-MM-dd) déjà exprimées dans le
// fuseau du module : on les reconstruit en date locale pour ne pas les décaler.
const parseYmd = (value) => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default (props) => {
  Utils.setBackground(false);

  const {intl} = props;
  const history = useHistory();
  const calendarRef = useRef();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleTimeZone, setModuleTimeZone] = useState();
  const [lengthUnit, setLengthUnit] = useState('KM');

  const [booking, setBooking] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createDate, setCreateDate] = useState();

  const [isNarrow, setIsNarrow] = useState(isNarrowNow);
  // Agenda par défaut sur toutes les tailles d'écran.
  const [view, setView] = useState('agenda');
  // Date « pivot » de la période affichée (yyyy-MM-dd). C'est nous qui la pilotons :
  // le headerToolbar natif de FullCalendar est désactivé, on lui pousse la date via
  // gotoDate() — le calendrier ne peut donc pas se désynchroniser de l'agenda.
  const [anchor, setAnchor] = useState(() => ymd(new Date()));
  const [calendarTitle, setCalendarTitle] = useState('');
  const [calendarRange, setCalendarRange] = useState();

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }
    refreshPlanning();
  }, []);

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }
    const query = window.matchMedia(NARROW_QUERY);
    const onChange = (event) => setIsNarrow(event.matches);
    // addListener : repli pour Safari < 14, qui n'a pas addEventListener sur MediaQueryList.
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
    } else {
      query.addListener(onChange);
    }
    return () => {
      if (query.removeEventListener) {
        query.removeEventListener('change', onChange);
      } else {
        query.removeListener(onChange);
      }
    };
  }, []);

  // Report de la date pivot sur le calendrier (aucun effet sur les vues maison).
  useEffect(() => {
    if (!isCalendarView(view)) {
      return;
    }
    const api = calendarRef.current && calendarRef.current.getApi();
    if (api) {
      api.gotoDate(anchor);
    }
  }, [anchor, view]);

  const refreshPlanning = () => {
    setLoading(true);
    setBooking(undefined);
    API.getPlanning(history).then(response => {
      if (response && response.data.success === true) {
        setEvents(response.data.data?.events || []);
        setLengthUnit(response.data.data.lengthUnit === 'MILE' ? 'MI' : 'KM');
        if (response.data.data.timeZone) {
          setModuleTimeZone(response.data.data.timeZone);
        }
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
      setLoading(false);
    });
  };

  const localeTag = useMemo(() => LOCALE_TAGS[Utils.getLanguage().toLowerCase()] || LOCALE_TAGS.fr, []);

  const calendarLocale = useMemo(() => CALENDAR_LOCALES[Utils.getLanguage().toLowerCase()] || CALENDAR_LOCALES.fr, []);

  const formatDate = (date, options) => {
    try {
      return new Intl.DateTimeFormat(localeTag, options).format(date);
    } catch (e) {
      return ymd(date);
    }
  };

  // « Aujourd'hui » se calcule dans le fuseau du module, pas celui du navigateur.
  const todayKey = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-CA', moduleTimeZone ? {timeZone: moduleTimeZone} : undefined)
        .format(new Date());
    } catch (e) {
      return ymd(new Date());
    }
  }, [moduleTimeZone]);

  const tomorrowKey = useMemo(() => {
    const d = parseYmd(todayKey);
    return ymd(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
  }, [todayKey]);

  // ── Période visible : plage réelle du calendrier, ou période des vues maison ──
  const range = useMemo(() => {
    const d = parseYmd(anchor);
    if (view === 'agenda') {
      return {
        start: ymd(new Date(d.getFullYear(), d.getMonth(), 1)),
        end: ymd(new Date(d.getFullYear(), d.getMonth() + 1, 1))
      };
    }
    if (view === 'year') {
      return {start: `${d.getFullYear()}-01-01`, end: `${d.getFullYear() + 1}-01-01`};
    }
    return calendarRange;
  }, [view, anchor, calendarRange]);

  const periodEvents = useMemo(() => {
    if (!range) {
      return [];
    }
    return events.filter(e => e.date >= range.start && e.date < range.end);
  }, [events, range]);

  // Une course aller-retour produit deux événements : on compte les réservations.
  const periodCount = useMemo(
    () => new Set(periodEvents.map(e => e.id)).size,
    [periodEvents]
  );

  const agendaDays = useMemo(() => {
    const byDate = new Map();
    periodEvents.forEach(e => {
      if (!byDate.has(e.date)) {
        byDate.set(e.date, []);
      }
      byDate.get(e.date).push(e);
    });
    return Array.from(byDate.keys()).sort().map(date => ({
      date,
      items: byDate.get(date).slice().sort((a, b) => (a.schedule || '').localeCompare(b.schedule || ''))
    }));
  }, [periodEvents]);

  // ── Vue Année : 12 mini-mois construits à la main ──
  const eventsByDate = useMemo(() => {
    const map = new Map();
    events.forEach(event => {
      if (!map.has(event.date)) {
        map.set(event.date, []);
      }
      map.get(event.date).push(event);
    });
    return map;
  }, [events]);

  // Initiales des jours, semaine commençant le lundi (dérivées de la locale active).
  // Le 1er janvier 2024 était un lundi.
  const weekdayInitials = useMemo(
    () => Array.from({length: 7}, (unused, i) => formatDate(new Date(2024, 0, 1 + i), {weekday: 'narrow'})),
    [localeTag]
  );

  const yearMonths = useMemo(() => {
    const year = parseYmd(anchor).getFullYear();
    const months = [];
    for (let m = 0; m < 12; m += 1) {
      const first = new Date(year, m, 1);
      const daysInMonth = new Date(year, m + 1, 0).getDate();
      const cells = [];
      // Décalage pour aligner le 1er sous le bon jour (grille lundi → dimanche).
      for (let i = (first.getDay() + 6) % 7; i > 0; i -= 1) {
        cells.push(null);
      }
      const monthIds = new Set();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const key = `${year}-${pad(m + 1)}-${pad(day)}`;
        const items = eventsByDate.get(key) || [];
        items.forEach(item => monthIds.add(item.id));
        cells.push({key, day, items, count: new Set(items.map(i => i.id)).size});
      }
      months.push({
        index: m,
        anchor: `${year}-${pad(m + 1)}-01`,
        label: formatDate(first, {month: 'long'}),
        count: monthIds.size,
        cells
      });
    }
    return months;
  }, [anchor, eventsByDate, localeTag]);

  const calendarEvents = useMemo(() => events.map(event => ({
    id: `${event.id}-${event.isReturn ? 'r' : 'a'}`,
    // FullCalendar trie par (start, durée, allDay, title) : préfixer le titre par
    // l'horaire garde les courses d'une même journée dans l'ordre chronologique.
    title: `${event.schedule || ''} ${event.name || ''}`.trim(),
    start: event.date,
    allDay: true,
    extendedProps: {event}
  })), [events]);

  // Le titre des vues calendrier vient de FullCalendar (déjà localisé) ; le repli
  // « mois année » couvre l'agenda et le laps avant le premier datesSet.
  const monthTitle = formatDate(parseYmd(anchor), {month: 'long', year: 'numeric'});
  const title = view === 'agenda' ? monthTitle
    : view === 'year' ? `${parseYmd(anchor).getFullYear()}`
      : (calendarTitle || monthTitle);

  // ─────────────────────────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────────────────────────
  const goToOffset = (direction) => {
    const d = parseYmd(anchor);
    if (view === 'dayGridWeek') {
      setAnchor(ymd(new Date(d.getFullYear(), d.getMonth(), d.getDate() + direction * 7)));
    } else if (view === 'year') {
      setAnchor(ymd(new Date(d.getFullYear() + direction, 0, 1)));
    } else {
      // Mois et agenda : on se cale sur le 1er pour éviter les débordements (31 → 3 mars).
      setAnchor(ymd(new Date(d.getFullYear(), d.getMonth() + direction, 1)));
    }
  };

  const selectView = (next) => {
    if (next === view) {
      return;
    }
    const api = calendarRef.current && calendarRef.current.getApi();
    // Semaine ↔ Mois : le composant reste monté, on bascule via l'API. Depuis/vers
    // une vue maison il est démonté/remonté et relit initialView + initialDate.
    if (isCalendarView(next) && isCalendarView(view) && api) {
      api.changeView(next, anchor);
    }
    setView(next);
  };

  const onDatesSet = (arg) => {
    const nextTitle = arg.view.title;
    const start = arg.startStr.slice(0, 10);
    const end = arg.endStr.slice(0, 10);
    setCalendarTitle(prev => (prev === nextTitle ? prev : nextTitle));
    setCalendarRange(prev => (prev && prev.start === start && prev.end === end ? prev : {start, end}));
  };

  // ─────────────────────────────────────────────────────────────
  // Interactions
  // ─────────────────────────────────────────────────────────────
  const openBooking = (bookingId) => {
    if (Number(booking?.id) === Number(bookingId)) {
      setShowDetailsModal(true);
      return;
    }
    API.getAdminBooking(history, bookingId).then(response => {
      if (response && response.data.success === true) {
        setBooking(response.data.data);
        setShowDetailsModal(true);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  const onEventClick = (arg) => openBooking(arg.event.extendedProps.event.id);

  // Clic sur une case vide : pré-remplit la date de la nouvelle réservation.
  const onDateClick = (arg) => {
    setCreateDate(arg.dateStr);
    setShowCreateModal(true);
  };

  const openCreateModal = () => {
    setCreateDate(undefined);
    setShowCreateModal(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Rendu
  // ─────────────────────────────────────────────────────────────
  const displayName = (event) => (event.blurred
    ? intl.formatMessage({id: 'BLURRED_BOOKING'})
    : event.name);

  const tooltipFor = (event) => [event.schedule, displayName(event), event.vehicle]
    .filter(Boolean).join(' · ');

  const directionLabel = (event) => intl.formatMessage({id: event.isReturn ? 'TRIP_RETURN' : 'TRIP_OUTBOUND'});

  const renderEventContent = (info) => {
    const {event} = info.event.extendedProps;
    const style = styleOf(event.status);
    const dimmed = event.status === 'CANCELED' || event.status === 'EXPIRED' ? ' is-canceled' : '';
    const name = displayName(event);
    const tooltip = tooltipFor(event);

    // Vue Mois : puce compacte (pastille + heure + nom), réduite à une barre sur mobile.
    if (info.view.type === 'dayGridMonth') {
      return (
        <div className={`ccv2-ev ccv2-ev-compact${dimmed}`} style={style} title={tooltip}>
          <span className="ccv2-ev-dot"/>
          <span className="ccv2-ev-ctime">{(event.schedule || '').slice(0, 5)}</span>
          {name && <span className="ccv2-ev-cname">{name}</span>}
        </div>
      );
    }

    // Vue Semaine : puce détaillée.
    return (
      <div className={`ccv2-ev ccv2-ev-full${dimmed}`} style={style} title={tooltip}>
        <span className="ccv2-ev-time">
          <FontAwesomeIcon icon={faClock}/>{event.schedule}
        </span>
        {name && (
          <span className="ccv2-ev-name">
            <FontAwesomeIcon icon={event.blurred ? faLock : faUser}/>
            <span>{name}</span>
          </span>
        )}
        <span className="ccv2-ev-meta">
          {event.vehicle && <span><FontAwesomeIcon icon={faCar}/>{event.vehicle}</span>}
          {event.passengers > 0 && <span><FontAwesomeIcon icon={faPeopleGroup}/>{event.passengers}</span>}
          {event.route === 'ROUND_TRIP' && (
            <span>
              <FontAwesomeIcon icon={event.isReturn ? faArrowLeft : faArrowRight}/>{directionLabel(event)}
            </span>
          )}
        </span>
      </div>
    );
  };

  const renderAgendaItem = (event) => {
    const {color, soft} = colorOf(event.status);
    const dimmed = event.status === 'CANCELED' || event.status === 'EXPIRED' ? ' is-canceled' : '';
    const name = displayName(event);
    const legend = LEGEND.find(l => l.status === event.status);
    const schedule = event.schedule || '';

    return (
      <button type="button"
              key={`${event.id}-${event.isReturn ? 'r' : 'a'}`}
              className={`ccv2-agenda-item${dimmed}`}
              style={{'--ev': color, '--ev-soft': soft}}
              onClick={() => openBooking(event.id)}>
        <span className="ccv2-agenda-time">
          <b>{schedule.slice(0, 5)}</b>
          <span>{schedule.slice(-5)}</span>
        </span>
        <span className="ccv2-agenda-body">
          <span className="ccv2-agenda-name">
            <FontAwesomeIcon icon={event.blurred ? faLock : faUser}/>
            <span>{name || '—'}</span>
          </span>
          <span className="ccv2-agenda-meta">
            {event.vehicle && <span><FontAwesomeIcon icon={faCar}/>{event.vehicle}</span>}
            {event.passengers > 0 && <span><FontAwesomeIcon icon={faPeopleGroup}/>{event.passengers}</span>}
            {event.route === 'ROUND_TRIP' && (
              <span>
                <FontAwesomeIcon icon={event.isReturn ? faArrowLeft : faArrowRight}/>{directionLabel(event)}
              </span>
            )}
          </span>
        </span>
        {legend && <span className="ccv2-agenda-pill"><FormattedMessage id={legend.labelId}/></span>}
      </button>
    );
  };

  const renderAgenda = () => {
    if (!agendaDays.length) {
      return (
        <div className="ccv2-plan-empty">
          <div className="ccv2-plan-empty-icon"><FontAwesomeIcon icon={faCalendarXmark}/></div>
          <div className="ccv2-plan-empty-title"><FormattedMessage id="PLANNING_EMPTY_TITLE"/></div>
          <div className="ccv2-plan-empty-desc"><FormattedMessage id="PLANNING_EMPTY_DESC"/></div>
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus}/> <FormattedMessage id="NEW_BOOKING"/>
          </button>
        </div>
      );
    }

    return (
      <div className="ccv2-agenda">
        {agendaDays.map(day => {
          const date = parseYmd(day.date);
          const isToday = day.date === todayKey;
          const relative = isToday ? 'TODAY' : (day.date === tomorrowKey ? 'TOMORROW' : null);
          return (
            <section className={`ccv2-agenda-day${isToday ? ' is-today' : ''}`} key={day.date}>
              <header className="ccv2-agenda-dayhead">
                <div className="ccv2-agenda-dnum">
                  <b>{date.getDate()}</b>
                  <span>{formatDate(date, {weekday: 'short'}).replace('.', '')}</span>
                </div>
                <div className="ccv2-agenda-dinfo">
                  <span className="ccv2-agenda-dlabel">
                    {relative
                      ? <FormattedMessage id={relative}/>
                      : formatDate(date, {weekday: 'long', day: 'numeric', month: 'long'})}
                  </span>
                  <span className="ccv2-agenda-dcount">
                    <FormattedMessage id="BOOKINGS_COUNT" values={{count: new Set(day.items.map(i => i.id)).size}}/>
                  </span>
                </div>
              </header>
              <div className="ccv2-agenda-items">
                {day.items.map(renderAgendaItem)}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  // Vue Année : 12 mini-mois cliquables. Un jour chargé porte jusqu'à 3 pastilles de
  // statut ; le clic bascule sur l'agenda du mois, où le détail est lisible.
  const renderYear = () => (
    <div className="ccv2-year">
      {yearMonths.map(month => (
        <section className={`ccv2-year-month${month.count ? '' : ' is-empty'}`} key={month.index}>
          <header className="ccv2-year-mhead">
            <button type="button" className="ccv2-year-mname"
                    onClick={() => { setAnchor(month.anchor); selectView('dayGridMonth'); }}>
              {month.label}
            </button>
            {month.count > 0 && <span className="ccv2-year-mcount">{month.count}</span>}
          </header>
          <div className="ccv2-year-dow" aria-hidden="true">
            {weekdayInitials.map((day, index) => <span key={index}>{day}</span>)}
          </div>
          <div className="ccv2-year-grid">
            {month.cells.map((cell, index) => {
              if (!cell) {
                return <span className="ccv2-year-cell is-blank" key={`blank-${index}`}/>;
              }
              const label = formatDate(parseYmd(cell.key), {weekday: 'long', day: 'numeric', month: 'long'});
              return (
                <button type="button"
                        key={cell.key}
                        disabled={!cell.count}
                        className={`ccv2-year-cell${cell.count ? ' has-events' : ''}${cell.key === todayKey ? ' is-today' : ''}`}
                        title={cell.count
                          ? `${label} — ${intl.formatMessage({id: 'BOOKINGS_COUNT'}, {count: cell.count})}`
                          : label}
                        onClick={() => { setAnchor(cell.key); setView('agenda'); }}>
                  <span className="ccv2-year-dnum">{cell.day}</span>
                  <span className="ccv2-year-dots">
                    {cell.items.slice(0, 3).map((event, i) => (
                      <span className="ccv2-year-dot" key={i} style={{background: colorOf(event.status).color}}/>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );

  const renderCalendar = () => (
    <FullCalendar
      ref={calendarRef}
      plugins={[dayGridPlugin, interactionPlugin]}
      initialView={view}
      initialDate={anchor}
      events={calendarEvents}
      locale={calendarLocale}
      timeZone={moduleTimeZone || 'local'}
      headerToolbar={false}
      height="auto"
      displayEventTime={false}
      fixedWeekCount={false}
      // Borne de densité : sans elle une journée chargée ferait exploser la hauteur de sa
      // ligne. En mois mobile (barres) et en semaine, les puces tiennent toutes.
      dayMaxEvents={view === 'dayGridMonth' && !isNarrow ? 3 : false}
      eventClick={onEventClick}
      dateClick={onDateClick}
      datesSet={onDatesSet}
      eventContent={renderEventContent}
    />
  );

  const canvasModifier = view === 'dayGridWeek' ? 'is-week'
    : view === 'dayGridMonth' ? 'is-month'
      : view === 'year' ? 'is-year' : 'is-agenda';

  const renderBody = () => {
    if (view === 'agenda') {
      return renderAgenda();
    }
    return view === 'year' ? renderYear() : renderCalendar();
  };

  return (
    <div className="ccv2-planning">
      <header className="ccv2-plan-pagehead">
        <PageHeader title="PLANNING" description="PLANNING_SUBTITLE"/>
        <div className="ccv2-toolbar">
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus}/> <FormattedMessage id="NEW_BOOKING"/>
          </button>
          <button type="button" className="ccv2-btn ccv2-btn-google" onClick={() => setShowGoogleModal(true)}>
            <FontAwesomeIcon icon={faGoogle}/> <FormattedMessage id="GOOGLE_CALENDAR"/>
          </button>
        </div>
      </header>

      <div className="ccv2-plan-card">
        <div className="ccv2-plan-head">
          <div className="ccv2-plan-nav">
            <button type="button" className="ccv2-plan-navbtn" onClick={() => goToOffset(-1)}
                    aria-label={intl.formatMessage({id: 'PREVIOUS_PERIOD'})}
                    title={intl.formatMessage({id: 'PREVIOUS_PERIOD'})}>
              <FontAwesomeIcon icon={faChevronLeft}/>
            </button>
            <button type="button" className="ccv2-plan-navbtn" onClick={() => goToOffset(1)}
                    aria-label={intl.formatMessage({id: 'NEXT_PERIOD'})}
                    title={intl.formatMessage({id: 'NEXT_PERIOD'})}>
              <FontAwesomeIcon icon={faChevronRight}/>
            </button>
            <button type="button" className="ccv2-plan-today" onClick={() => setAnchor(todayKey)}>
              <FontAwesomeIcon icon={faCalendarDay}/> <FormattedMessage id="TODAY"/>
            </button>
          </div>

          <h2 className="ccv2-plan-title">{title}</h2>

          <div className="ccv2-plan-views" role="tablist">
            {VIEWS.map(item => (
              <button type="button"
                      key={item.key}
                      role="tab"
                      aria-selected={view === item.key}
                      className={`ccv2-plan-view${view === item.key ? ' is-active' : ''}`}
                      onClick={() => selectView(item.key)}>
                <FontAwesomeIcon icon={item.icon} className="ccv2-plan-view-icon"/>
                <FormattedMessage id={item.labelId}/>
              </button>
            ))}
          </div>
        </div>

        <div className={`ccv2-plan-canvas ${canvasModifier}`}>
          {/* Spinner au premier chargement seulement : sur un rafraîchissement on garde
              le calendrier affiché, sinon il serait démonté/remonté à chaque action. */}
          {loading && !events.length
            ? <div className="ccv2-plan-loading">
                <FontAwesomeIcon icon={faSpinner} spin/> <FormattedMessage id="LOADING"/>
              </div>
            : renderBody()}
        </div>

        <div className="ccv2-plan-foot">
          <div className="ccv2-plan-legend">
            {LEGEND.map(item => (
              <span className="ccv2-plan-legend-item" key={item.status}>
                <span className="ccv2-plan-legend-dot" style={{background: colorOf(item.status).color}}/>
                <FormattedMessage id={item.labelId}/>
              </span>
            ))}
          </div>
          {!loading && range && (
            <div className="ccv2-plan-count">
              <FormattedMessage id="BOOKINGS_COUNT" values={{count: periodCount}}/>
            </div>
          )}
        </div>
      </div>

      {booking &&
        <BookingModal intl={intl} lengthUnit={lengthUnit} booking={booking} show={showDetailsModal}
                      onClose={() => setShowDetailsModal(false)} onRefresh={() => refreshPlanning()}/>
      }
      <BookingCreateModal intl={intl} lengthUnit={lengthUnit} show={showCreateModal} initialDate={createDate}
                          onClose={() => setShowCreateModal(false)} onRefresh={() => refreshPlanning()}/>
      <GoogleModal history={history} intl={intl} show={showGoogleModal} onClose={() => setShowGoogleModal(false)}/>
    </div>
  );
};
